import { createSSRApp, createApp as createClientApp, watch } from 'vue';
import App from './App.vue';
import { createRouter, createWebHistory, createMemoryHistory } from 'vue-router';
import { createI18n } from 'vue-i18n';
import { routes } from './router';
import { CONFIG } from './config';
import './assets/css/main.css';

// Published snapshots from DB (regenerate: npm run i18n:export)
import {
  publishedLocaleMessages,
  publishedPropertyContentByLocale,
  publishedTranslationFingerprints
} from './i18n/publishedBundles.js';
import {
  syncPublishedLocaleMessagesIfNeeded
} from './utils/i18nRemoteSync.js';

// Auth store for navigation guards (only used client-side)
import { authStore } from './stores/auth';
import { spaLocaleHomePath } from './utils/spaLocaleHomePath';
import { mergeLocaleMessagesDeep } from './utils/mergeLocaleMessages.js';
import { syncDocumentHtmlLang } from './utils/documentHtmlLang.js';
import { escapeVueI18nAtInTranslationsTree } from '@merlot/common/vueI18nMessageEscaping.js';

/** Baseline vue-i18n messages per locale (Phase E — same data as GET /v1/i18n at export time). */
const fallbackMessages = publishedLocaleMessages;

/**
 * Extract locale from URL path
 * e.g., '/fr/property' -> 'fr', '/en' -> 'en'
 */
function extractLocaleFromPath(path) {
  const parts = path.split('/').filter(Boolean);
  if (parts.length > 0 && CONFIG.SUPPORTED_LOCALES.includes(parts[0])) {
    return parts[0];
  }
  return CONFIG.DEFAULT_LOCALE;
}

/**
 * Create Vue app instance for SSG/SSR or client
 * 
 * @param {Object} options
 * @param {boolean} options.isSSR - Whether this is server-side rendering/generation
 * @param {string} options.url - Current URL (for SSR/SSG)
 * @param {Object} options.initialState - Initial state from server (translations, content, user)
 * @returns {{ app, router, i18n }}
 */
export function createApp({ isSSR = false, url = '/', initialState = {} } = {}) {
  // Create app instance
  const app = isSSR ? createSSRApp(App) : createClientApp(App);

  // Determine locale from URL path, initialState, or client storage
  const pathLocale = extractLocaleFromPath(url);
  let locale = initialState.locale || pathLocale;
  
  // If no locale from URL/initialState (SPA routes), get from cookie/browser
  if (!locale && !isSSR) {
    locale = CONFIG.getLocaleFromClient();
  }
  
  // Final fallback to default
  locale = locale || CONFIG.DEFAULT_LOCALE;

  // i18n merge order + SEO/content pipeline: see frontend/docs/SEO_AND_CONTENT.md
  // Build messages object using database translations when available
  // Database translations override bundled fallback messages
  const messages = {};
  for (const loc of CONFIG.SUPPORTED_LOCALES) {
    // Start with bundled fallback messages
    messages[loc] = { ...fallbackMessages[loc] };
    
    // If we have database translations for this locale, merge them
    // For SSG pages, translations are in initialState.translations for the current locale
    // For SPA pages, we fetch translations in entry-client.js and they're in initialState
    if (initialState.translations && Object.keys(initialState.translations).length > 0) {
      // Only merge if this is the locale we have translations for
      // (translations object is for a specific locale, not all locales)
      if (loc === (initialState.locale || locale)) {
        // Deep-merge so partial API/DB trees (e.g. only auth.*) do not wipe bundled nav.*, etc.
        // Escape: embedded JSON may use plain `@`; API may already return storage form (idempotent).
        messages[loc] = mergeLocaleMessagesDeep(
          messages[loc],
          escapeVueI18nAtInTranslationsTree(initialState.translations)
        );
      }
    }
  }

  // Create i18n instance
  const i18n = createI18n({
    locale,
    fallbackLocale: CONFIG.DEFAULT_LOCALE,
    messages,
    legacy: false,
    globalInjection: true
  });

  // Create router instance
  const router = createRouter({
    history: isSSR ? createMemoryHistory() : createWebHistory(),
    routes,
    scrollBehavior(to, from, savedPosition) {
      if (savedPosition) {
        return savedPosition;
      }
      return { top: 0 };
    }
  });

  // Navigation guards (only for client-side, SSG doesn't need auth during build)
  if (!isSSR) {
    // Sync i18n locale with route on navigation
    router.afterEach(async (to) => {
      const routeLang = to.params.lang;
      let newLocale = i18n.global.locale.value;

      if (routeLang && CONFIG.SUPPORTED_LOCALES.includes(routeLang)) {
        newLocale = routeLang;
      } else {
        // For SPA routes without locale in URL, use getLocaleFromClient helper
        newLocale = CONFIG.getLocaleFromClient();
      }

      const bundledFp = publishedTranslationFingerprints[newLocale] || '';

      // Refresh for the effective locale on every navigation so admin picks up publishes without F5.
      await syncPublishedLocaleMessagesIfNeeded({
        locale: newLocale,
        fallbackMessages,
        bundledFingerprint: bundledFp,
        setLocaleMessage: (loc, msgs) => i18n.global.setLocaleMessage(loc, msgs)
      });

      if (newLocale !== i18n.global.locale.value) {
        i18n.global.locale.value = newLocale;
      }
    });

    router.beforeEach(async (to, from, next) => {
      const requiresAuth = to.meta.requiresAuth;
      const requiresAdmin = to.meta.requiresAdmin;
      const hideWhenAuth = to.meta.hideWhenAuth;
      const isAuthenticated = authStore.isAuthenticated;
      const userRole = authStore.user?.role;

      // Hide login page when already authenticated (full access only).
      // Pending / deleted users keep a session but stay on /login to see status + sign out.
      if (hideWhenAuth && isAuthenticated) {
        if (authStore.isAdmin) {
          return next('/admin');
        }
        if (authStore.isTenant) {
          return next('/dashboard');
        }
        return next();
      }

      // Check authentication requirement
      if (requiresAuth && !isAuthenticated) {
        // Redirect to login with return URL (except if already going to login)
        const returnUrl = to.path !== '/login' ? to.fullPath : undefined;
        const loginPath = returnUrl ? `/login?return=${encodeURIComponent(returnUrl)}` : '/login';
        return next(loginPath);
      }

      // Redirect admins away from tenant dashboard (all nested /dashboard/* paths)
      if (
        isAuthenticated &&
        authStore.isAdmin &&
        (to.path === '/dashboard' || to.path.startsWith('/dashboard/'))
      ) {
        return next('/admin');
      }

      const requiresTenant = to.matched.some((record) => record.meta.requiresTenant);
      if (requiresTenant && isAuthenticated && !authStore.isTenant) {
        return next(spaLocaleHomePath());
      }

      // Check admin requirement
      if (requiresAdmin && userRole !== 'admin') {
        if (authStore.isTenant) {
          return next('/dashboard');
        }
        const returnUrl = to.path !== '/login' ? to.fullPath : undefined;
        const loginPath = returnUrl ? `/login?return=${encodeURIComponent(returnUrl)}` : '/login';
        return next(loginPath);
      }

      next();
    });
  }

  // Provide initial state to the app
  app.provide('initialState', initialState);
  app.provide('publishedPropertyContentByLocale', publishedPropertyContentByLocale);
  app.provide('isSSR', isSSR);
  app.provide('currentLocale', locale);

  // Use plugins
  app.use(router);
  app.use(i18n);

  if (!isSSR) {
    watch(
      () => i18n.global.locale.value,
      (loc) => {
        syncDocumentHtmlLang(loc);
      },
      { immediate: true }
    );
  }

  // Set router instance for useApi 401 handling (client-side only)
  if (!isSSR) {
    // Use dynamic import to avoid SSR issues, but handle it asynchronously
    import('./composables/useApi.js').then(({ setApiRouter }) => {
      setApiRouter(router);
    }).catch(err => {
      console.warn('Failed to set API router:', err);
    });
  }

  return { app, router, i18n };
}

export { routes };
